import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
  Table,
  TableBody,
  TableHead,
  TableContainer,
  Button,
  TableRow,
  Paper,
  Typography,
  Link,
  Tooltip,
  Stack,
  TextField,
  Box,
  Dialog, 
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { deleteOrganization, getOrganizationList } from "../apis/apiList/organizationApi";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from '@mui/icons-material/Edit';
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import moment from "moment";
import { useMessage } from "../context/MessageContext";
import { useApp } from "../context/AppContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#495967",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const CustomPaginationItem = styled(PaginationItem)(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.primary.main : "#495967",
  color: "#fff",
  borderRadius: "0px",
  fontWeight: selected ? "bold" : "normal",
  margin: "0 4px",
  maxHeight: "26px",
  padding: "0px",
  "&:hover": {
    backgroundColor: selected ? "#d40000" : "#1e2a33",
  },
  "&.Mui-selected": {
    backgroundColor: selected ? theme.palette.primary.main : "#495967",
    color: "#fff",
    padding: "0px",
  },
}));

const subscriberTypeList = [
  { id: 1, type: "Leads", value: "lead" },
  { id: 2, type: "Prospects", value: "prospects" },
  { id: 3, type: "Demo", value: "demo" },
  { id: 4, type: "Subscriber", value: "subscriber" },
  { id: 5, type: "Subscriber - Canceled", value: "subscriber-canceled" },
];

function OrganizationList() {
  const navigation = useNavigate();
  const {showMessage} = useMessage()
  const [originalOrganizationList, setOriginalOrganizationList] = useState();
  const [organizationList, setOrganizationList] = useState([]);
  const [paginatedOrganizationList, setPaginatedOrganizationList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const {userDetails} = useApp();
  const [selectedDeleteOrganization, setSelectedDeleteOrganization] = useState({});
  const [selectedSubscriberType, setSelectedSubscriberType] = useState("");

  const handleDeleteDialog = (org) => {
    setSelectedDeleteOrganization(org)
    setOpen(true)
  };
  const handleClose = () => setOpen(false);

  const handleDelete = async () => {
    try {
      const orgResponse = await deleteOrganization(selectedDeleteOrganization._id);
      console.log('orgResponse', orgResponse);
      if(orgResponse.success) {
        handleDeleteFilter(selectedDeleteOrganization._id)
        showMessage(orgResponse.message, 'success')
        setSelectedDeleteOrganization({});
        handleClose()
      }
    } catch (err) {
      console.log('err', err)
      if (err?.response) {
        showMessage(err?.response?.data?.message, "error");
      } else {
        showMessage(err?.message, "error");
      }
    }

      // handleClose();
  };


  useEffect(() => {
    async function fetchProjectList() {
      try {
        let organizations = await getOrganizationList(userDetails._id);
        console.log('organizations', organizations)
        if (organizations?.data.length > 0) {
          const filteredRows = searchQuery
            ? organizations?.data.filter((org) =>
              org.projectName
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              )
            : organizations?.data;

          setOrganizationList(filteredRows);
          handlePagination(filteredRows, page);
        }

        setOriginalOrganizationList(organizations?.data ? organizations?.data : []);
      } catch (err) {
        console.log(err);
      }
    }
    fetchProjectList();
  }, []);

  const handleSearchChange = (event) => {
    const str = event.target.value.trim();
    setSearchQuery(str);

    const filteredRows = originalOrganizationList.filter((org) =>
      org.organizationName.toLowerCase().includes(str.toLowerCase())
    );
    setOrganizationList(filteredRows);
    handlePagination(filteredRows, 1);
    setPage(1);
  };

  const handleChange = (event, value) => {
    setPage(value);
    handlePagination(organizationList, value);
  };

  const handlePagination = (organizationList, pageNum) => {
    const startIndex = (pageNum - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const pageList = organizationList.slice(startIndex, endIndex);
    setPaginatedOrganizationList(pageList);
  };

  const handleDeleteFilter = (id) =>{
    const newList = paginatedOrganizationList.filter((org)=>org._id !== id)
    setPaginatedOrganizationList(newList);
  }

  const handleFilter = (status) => {
    let filteredRows = [];
    
    if (!status) {
      filteredRows = originalOrganizationList;
    } else {
      filteredRows = originalOrganizationList.filter(
        (org) => org.subscriberType === status
      );
    }

    setOrganizationList(filteredRows);
    handlePagination(filteredRows, 1);
    setPage(1);
  };

  return (
    <div>
      <Stack
        sx={{ backgroundColor: "#495967" }}
        py={1.2}
        px={4}
        direction={"row"}
        justifyContent={"space-between"}
      >
        <Typography variant="h2" color="#fff" width={"55%"}>
          Organization List
        </Typography>
        <Stack direction={"row"} gap={3} width={"45%"}>
          <Box width={"65%"}>
            <div>
              <input
                type="text"
                className="org-search"
                placeholder="Search"
                onChange={handleSearchChange}
              />
            </div>
          </Box>
          <Button
            variant="contained"
            onClick={() => {
              navigation("/create/organization");
            }}
            sx={{ textTransform: "capitalize", width: "35%" }}
          >
            Create Organization
          </Button>
        </Stack>
      </Stack>
      <Box backgroundColor="rgb(28, 47, 65)" p={4}>
        <Box p={4} backgroundColor="#fff" borderRadius={1}>
          <Box mb={2} >
            <FormControl size="small" sx={{ minWidth: 250, backgroundColor: "white" }}>
              <InputLabel>Subscriber Type</InputLabel>
              <Select
                value={selectedSubscriberType}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedSubscriberType(value);
                  handleFilter(value);
                }}
                label="Subscriber Type"
                sx={{
                  "& .MuiSelect-select": {
                    padding: "10px 14px !important",
                  },
                }}
              >
                <MenuItem value="">All</MenuItem>
                {subscriberTypeList.map((item) => (
                  <MenuItem key={item.id} value={item.value}>
                    {item.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper} sx={{ mb: "20px" }}>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Organization Name</StyledTableCell>
                  <StyledTableCell align="left">Street</StyledTableCell>
                  <StyledTableCell align="left">City</StyledTableCell>
                  <StyledTableCell align="left">State</StyledTableCell>
                  <StyledTableCell align="left">Zip Code</StyledTableCell>
                  <StyledTableCell align="left">Phone</StyledTableCell>
                  <StyledTableCell align="left">Twitter</StyledTableCell>
                  <StyledTableCell align="left">Linked in</StyledTableCell>
                  <StyledTableCell align="left">Create At</StyledTableCell>
                  <StyledTableCell align="center">Action</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrganizationList &&
                paginatedOrganizationList.length > 0 ? (
                  paginatedOrganizationList.map((org, index) => (
                    <>
                      <StyledTableRow
                        key={org._id}
                        sx={{ backgroundColor: "rgba(0,0,0,.05)" }}
                      >
                        <StyledTableCell
                          component="th"
                          scope="row"
                          align="left"
                          sx={{cursor: 'pointer'}}
                          onClick={()=>{navigation(`/org/${org._id}/projects`)}}
                        >
                          {org.organizationName}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {org.street}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {org.city}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {org.state}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {org.zip}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {org.phone}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {org.twitter}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {org.linkedin}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {moment(org.createdAt).format("YYYY-MM-DD")}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <Stack direction={"row"} gap={4}>
                            <CancelIcon
                              style={{ color: "red", cursor: 'pointer' }}
                              onClick={()=>{handleDeleteDialog(org)}}
                            />
                            <EditIcon
                              style={{
                                backgroundColor: "#007bff",
                                color: "#fff",
                                borderRadius: "50%",
                                padding: "4px",
                                fontSize: "22px",
                              }}
                              onClick={() => {
                                navigation(`/organization/edit/${org._id}`);
                              }}
                            />
                          </Stack>
                        </StyledTableCell>
                      </StyledTableRow>
                    </>
                  ))
                ) : (
                  <StyledTableRow sx={{ backgroundColor: "rgba(0,0,0,.05)" }}>
                    <StyledTableCell align="left">
                      <Box> No Organization found</Box>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* <Pagination count={10} showFirstButton showLastButton /> */}
          <Pagination
            count={Math.ceil(organizationList.length / rowsPerPage)}
            page={page}
            onChange={handleChange}
            renderItem={(item) => <CustomPaginationItem {...item} />}
            siblingCount={1}
            boundaryCount={1}
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>

      {/* Dialog for delete organization */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle> Are you sure you want to delete <strong> {selectedDeleteOrganization.organizationName} </strong> Organization? </DialogTitle>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default OrganizationList;
